import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Digit Span Backward — composant jeu principal.
 *
 * Paradigme : WAIS-IV WMS (Wechsler 2008), variante backward du Digit Span.
 * Pattern minimal Sprint 2 (cf. _template/) sans LevelSelector ni PedagogieDrawer.
 */
import { useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ActionBarProvider, ActionBarSlot, GameShell, PhaseRenderer, useActionBar, useGameShellFromContext, } from '@labrain/game-shell';
import { createDigitSpanBackwardAdapter, makeStimulus, } from './adapter';
import { Demo } from './demo';
import { DSBDebriefView, DSBStaircaseView } from './staircaseView';
import { paradigmMeta, staircaseConfig, variantForLevel } from './params';
const GAME_SLUG = 'digit-span-backward';
function Intro({ level, onShowDemo, onSkipDemo }) {
    const { t } = useTranslation(['games', 'common']);
    const variant = variantForLevel(level);
    useActionBar({
        primary: (_jsx("button", { type: "button", onClick: onShowDemo, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.see_demo') })),
        secondary: (_jsx("button", { type: "button", onClick: onSkipDemo, className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.start_direct') })),
    });
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s5 py-s6 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: paradigmMeta.paradigm }), _jsx("h1", { className: "font-display text-[28px] font-medium leading-9 tracking-cy-display text-cyan-text", children: t('digit_span_backward.title') }), _jsx("p", { className: "max-w-[320px] text-[14.5px] leading-5 text-cyan-text-secondary", children: t('digit_span_backward.intro_description') }), _jsxs("div", { className: "grid w-full grid-cols-2 gap-s3 border border-cyan-border-faint bg-cyan-surface p-s4 text-start text-[12px] text-cyan-text-secondary", children: [_jsxs("div", { children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('digit_span_backward.fields.level') }), _jsx("span", { className: "text-[14px] text-cyan-text", children: t('digit_span_backward.level_with_label', { level, label: variant.label }) })] }), _jsxs("div", { children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('common:metrics.trials') }), _jsx("span", { className: "text-[14px] text-cyan-text", children: t('common:labels.trials_max', { n: staircaseConfig.maxTrials }) })] })] }), _jsx(Link, { to: "/library", className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.back_to_play') })] }));
}
function IntroSlot({ level }) {
    const shell = useGameShellFromContext();
    return (_jsx(Intro, { level: level, onShowDemo: () => shell.startSession(), onSkipDemo: () => shell.skipDemo() }));
}
function DemoSlot({ level }) {
    const shell = useGameShellFromContext();
    return (_jsx(Demo, { level: level, onComplete: () => shell.markDemoDone(), onSkip: () => shell.markDemoDone() }));
}
function WrapUpSlot() {
    const shell = useGameShellFromContext();
    useMemo(() => {
        try {
            window.localStorage.setItem(`labrain.${GAME_SLUG}.lastRun`, JSON.stringify({
                ts: Date.now(),
                threshold: shell.snapshot.staircase.thresholdEstimate,
            }));
        }
        catch {
            /* ignored */
        }
        shell.markWrapUpDone();
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
}
export function DigitSpanBackwardGame() {
    const navigate = useNavigate();
    const level = 1;
    const levelRef = useRef(level);
    levelRef.current = level;
    const adapterRef = useRef(null);
    if (adapterRef.current === null) {
        adapterRef.current = createDigitSpanBackwardAdapter(level);
    }
    const adapter = adapterRef.current;
    const config = useMemo(() => ({
        gameSlug: GAME_SLUG,
        gameMaster: 'eau',
        staircaseConfig,
        showDemo: true,
        showCalibrationWarmup: false,
        enableHaptics: true,
        enableSound: true,
        enableVisualFeedback: true,
    }), []);
    return (_jsxs(ActionBarProvider, { children: [_jsx(GameShell, { config: config, adapter: adapter, buildStimulus: (engStim) => makeStimulus(engStim.trialIdx, engStim.value, levelRef.current), grader: (_stim, resp) => ({
                    correct: resp.correct,
                    reactionTimeMs: resp.reactionTimeMs,
                    rawInput: {
                        picked: resp.picked,
                        truthReversed: resp.truthReversed,
                        hits: resp.hits,
                    },
                }), children: _jsx(PhaseRenderer, { intro: _jsx(IntroSlot, { level: level }), demo: _jsx(DemoSlot, { level: level }), staircase: _jsx(DSBStaircaseView, { adapter: adapter, level: level, onAbort: () => navigate('/library') }), wrapUp: _jsx(WrapUpSlot, {}), debrief: _jsx(DSBDebriefView, { level: level, onAbort: () => navigate('/library') }), aborted: null }) }), _jsx(ActionBarSlot, {})] }));
}
export default DigitSpanBackwardGame;
//# sourceMappingURL=game.js.map